import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Products() {
  const [selectedProduct, setSelectedProduct] = useState<'powder' | 'tablets'>('powder');

  const products = {
    powder: {
      name: 'Moringa Powder',
      tagline: 'Superfood nutrition from nature\'s green powerhouse',
      price: '₹299',
      originalPrice: '₹399',
      image: 'https://static.readdy.ai/image/9d639ef6c9792709f408f68c264ec65c/259510fa1447af2896f61d3473620b43.png',
      benefits: ['Boosts immunity & energy', 'Supports digestion', 'Rich in antioxidants', '100% organic & vegan'],
      usage: '1–2 tsp in smoothies, juices, milk, or yogurt daily; can be added to soups or baked recipes',
      faqs: [
        { q: 'Is it safe for children?', a: 'Yes, Moringa powder is safe for children over 2 years. Start with smaller amounts.' },
        { q: 'Is it additive-free?', a: 'Absolutely! Our Moringa powder contains no additives, preservatives, or artificial ingredients.' },
        { q: 'Can I use it in hot drinks?', a: 'Yes, you can add it to warm beverages, but avoid boiling to preserve nutrients.' }
      ]
    },
    tablets: {
      name: 'Moringa Tablets',
      tagline: 'Daily wellness in a convenient, plant-based capsule',
      price: '₹399',
      originalPrice: '₹499',
      image: 'https://static.readdy.ai/image/9d639ef6c9792709f408f68c264ec65c/ec501008434a48d659f10ea9ddd79ab9.png',
      benefits: ['Supports immunity & energy', 'Easy daily supplement', '100% organic & chemical-free', 'Convenient on-the-go'],
      usage: '1–2 tablets daily after meals with water',
      faqs: [
        { q: 'Are they vegan?', a: 'Yes, our tablets are 100% vegan and made from pure Moringa leaf powder.' },
        { q: 'Can I take them with other supplements?', a: 'Generally yes, but consult your healthcare provider if you take medications.' },
        { q: 'Are they preservative-free?', a: 'Yes, our tablets contain no artificial preservatives or harmful chemicals.' }
      ]
    }
  };

  const currentProduct = products[selectedProduct];

  return (
    <div className="min-h-screen">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="py-11 bg-emerald-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">Our Products</h1>
            <p className="text-xl text-gray-600">
              Discover the power of Moringa with our premium, organic products designed 
              to support your wellness journey naturally.
            </p>
          </div>
        </section>

        {/* Product Selection */}
        <section className="py-12 bg-white">
          <div className="max-w-2xl mx-auto px-6 lg:px-8">
            <div className="flex bg-emerald-50 rounded-full p-2">
              <button
                onClick={() => setSelectedProduct('powder')}
                className={`flex-1 py-3 px-6 rounded-full font-semibold transition-all cursor-pointer ${
                  selectedProduct === 'powder' 
                    ? 'bg-emerald-600 text-white shadow-lg' 
                    : 'text-emerald-600 hover:bg-emerald-100'
                }`}
              >
                Moringa Powder
              </button>
              <button
                onClick={() => setSelectedProduct('tablets')}
                className={`flex-1 py-3 px-6 rounded-full font-semibold transition-all cursor-pointer ${
                  selectedProduct === 'tablets' 
                    ? 'bg-emerald-600 text-white shadow-lg' 
                    : 'text-emerald-600 hover:bg-emerald-100'
                }`}
              >
                Moringa Tablets
              </button>
            </div>
          </div>
        </section>

        {/* Product Details */}
        <section className="py-16 bg-white" data-product-shop>
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <img 
                  src={currentProduct.image}
                  alt={currentProduct.name}
                  className="w-full max-w-lg mx-auto rounded-2xl shadow-xl object-cover"
                />
              </div>
              
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">{currentProduct.name}</h2>
                <p className="text-xl text-emerald-600 font-medium mb-6">{currentProduct.tagline}</p>
                
                <div className="flex items-center gap-4 mb-8">
                  <span className="text-4xl font-bold text-emerald-600">{currentProduct.price}</span>
                  <span className="text-2xl text-gray-500 line-through">{currentProduct.originalPrice}</span>
                  <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-medium">
                    25% OFF
                  </span>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Benefits:</h3>
                  <ul className="space-y-3">
                    {currentProduct.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <i className="ri-check-line w-6 h-6 text-emerald-600 mr-3 flex-shrink-0"></i>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">How to Use:</h3>
                  <p className="text-gray-600 bg-emerald-50 p-4 rounded-lg">{currentProduct.usage}</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button variant="primary" size="lg" className="flex-1">
                    Add to Cart
                  </Button>
                  <Button variant="outline" size="lg">
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-emerald-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-6">
              {currentProduct.faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.q}</h3>
                  <p className="text-gray-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nutrition Facts */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Moringa Nutrition Facts
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="bg-emerald-50 rounded-xl p-6">
                <div className="text-3xl font-bold text-emerald-600 mb-2">7x</div>
                <div className="text-gray-600">More Vitamin C than Oranges</div>
              </div>
              
              <div className="bg-emerald-50 rounded-xl p-6">
                <div className="text-3xl font-bold text-emerald-600 mb-2">4x</div>
                <div className="text-gray-600">More Calcium than Milk</div>
              </div>
              
              <div className="bg-emerald-50 rounded-xl p-6">
                <div className="text-3xl font-bold text-emerald-600 mb-2">3x</div>
                <div className="text-gray-600">More Iron than Spinach</div>
              </div>
              
              <div className="bg-emerald-50 rounded-xl p-6">
                <div className="text-3xl font-bold text-emerald-600 mb-2">2x</div>
                <div className="text-gray-600">More Protein than Yogurt</div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
